/**
 * Shared utilities for workspace standards components.
 *
 * StatusBadge, status style map, and date formatter used by
 * both WorkspaceStandardCard and WorkspaceStandardDetailPage.
 */

import { useTranslation } from "@/packages/utils";

/* ------------------------------------------------------------------ */
/* Status styles                                                       */
/* ------------------------------------------------------------------ */

export const STATUS_STYLES: Record<
  string,
  { bgColor: string; textColor: string; pulse?: boolean }
> = {
  extracting: {
    bgColor: "rgba(234,179,8,0.15)",
    textColor: "#ca8a04",
    pulse: true,
  },
  extracted: {
    bgColor: "rgba(34,197,94,0.15)",
    textColor: "#22c55e",
  },
  extraction_failed: {
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
  generating_checklist: {
    bgColor: "rgba(234,179,8,0.15)",
    textColor: "#ca8a04",
    pulse: true,
  },
  approved: {
    bgColor: "rgba(99,102,241,0.15)",
    textColor: "#6366f1",
  },
  checklist_generation_failed: {
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
  published: {
    bgColor: "rgba(34,197,94,0.15)",
    textColor: "#16a34a",
  },
};

export const DEFAULT_STYLE = {
  bgColor: "rgba(161,161,166,0.15)",
  textColor: "#a1a1a6",
};

/* ------------------------------------------------------------------ */
/* StatusBadge                                                         */
/* ------------------------------------------------------------------ */

export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation("common");
  const style = STATUS_STYLES[status] ?? DEFAULT_STYLE;

  const labelMap: Record<string, string> = {
    extracting: t("workspaceStandards.status.extracting"),
    extracted: t("workspaceStandards.status.extracted"),
    extraction_failed: t("workspaceStandards.status.extraction_failed"),
    generating_checklist: t("workspaceStandards.status.generating_checklist"),
    approved: t("workspaceStandards.status.approved"),
    checklist_generation_failed: t(
      "workspaceStandards.status.checklist_generation_failed",
    ),
    published: t("workspaceStandards.status.published"),
  };
  const label = labelMap[status] ?? t("workspaceStandards.status.unknown");

  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium ${style.pulse ? "animate-pulse" : ""}`}
      style={{ backgroundColor: style.bgColor, color: style.textColor }}
    >
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Date formatter                                                      */
/* ------------------------------------------------------------------ */

export function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
